// IMPORTANT: We are currently working on the new nav v4 (bbc_nav_v4)
// This code is just for the create button in nav 3. If you are making
// any changes in this file, make sure you add the equivilant to top-nav-create-button.tsx
// or your changes will not be in the new navigation (nav v4).
import { useState, useContext, useCallback, ReactNode, memo } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import ExpandIcon from '@atlaskit/icon/core/migration/chevron-down';
import { PopupMenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';

import { HorizontalNavContext } from './horizontal-nav-context';
import { HorizontalNavLinkItem } from './horizontal-nav-link-item';
import messages from './horizontal-nav.i18n';
import * as styled from './horizontal-nav.style';

type Props = {
  intl: InjectedIntl;
  isBeingRenderedInsideMobileNav?: boolean;
  children?: ReactNode;
};

const HorizontalCreateBase = (props: Props) => {
  const {
    globalBitbucketActions,
    containerBitbucketActions,
    linkComponent,
    isLoggedIn,
    publishUiEvent,
  } = useContext(HorizontalNavContext);
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);
  const onOpen = useCallback(() => {
    setIsOpen(prev => !prev);
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'navigationItem',
      actionSubjectId: 'createButton',
      source: 'navigation',
      attributes: {
        itemIsGlobalHorizontalNav: true,
      },
    });
  }, [setIsOpen, publishUiEvent]);

  const renderMenu = useCallback(
    () => (
      <PopupMenuGroup minWidth={149}>
        {globalBitbucketActions && globalBitbucketActions.length && (
          <Section>
            {globalBitbucketActions.map(menuItem => (
              <HorizontalNavLinkItem
                linkComponent={linkComponent}
                key={menuItem.url}
                href={menuItem.url}
                isClientLink={menuItem.is_client_link}
                target={menuItem.target}
                testId={menuItem?.test_id || undefined}
                analyticsLabel={menuItem.analytics_label}
                actionSubject="navigationGlobalCreateItem"
                publishUiEvent={publishUiEvent}
              >
                {menuItem.label}
              </HorizontalNavLinkItem>
            ))}
          </Section>
        )}
        {containerBitbucketActions && !!containerBitbucketActions.length && (
          <Section
            title={props.intl.formatMessage(messages.repositorySectionHeading)}
          >
            {containerBitbucketActions.map(menuItem => (
              <HorizontalNavLinkItem
                linkComponent={linkComponent}
                key={menuItem.url}
                href={menuItem.url}
                isClientLink={menuItem.is_client_link}
                target={menuItem.target}
                testId={menuItem?.test_id || undefined}
                analyticsLabel={menuItem.analytics_label}
                actionSubject="navigationContainerCreateItem"
                publishUiEvent={publishUiEvent}
                onClose={onClose}
              >
                {menuItem.label}
              </HorizontalNavLinkItem>
            ))}
          </Section>
        )}
      </PopupMenuGroup>
    ),
    [
      globalBitbucketActions,
      containerBitbucketActions,
      linkComponent,
      publishUiEvent,
      props.intl,
    ]
  );

  const renderTriggerButton = useCallback(
    (triggerProps: any) => (
      <styled.HorizontalNavItem>
        {props.isBeingRenderedInsideMobileNav ? (
          <IconButton
            appearance="primary"
            icon={EditorAddIcon}
            {...triggerProps}
            isSelected={isOpen}
            aria-label={props.intl.formatMessage(
              messages.createButtonAriaLabel
            )}
            testId="mobile-create"
            id="createGlobalItem"
            onClick={onOpen}
          />
        ) : (
          <Button
            {...triggerProps}
            appearance="primary"
            isSelected={isOpen}
            aria-label={props.intl.formatMessage(
              messages.createButtonAriaLabel
            )}
            testId="create-button"
            id="createGlobalItem"
            onClick={onOpen}
            iconAfter={ExpandIcon}
          >
            {props.intl.formatMessage(messages.createButtonLabel)}
          </Button>
        )}
      </styled.HorizontalNavItem>
    ),
    [isOpen, onOpen, props.intl, props.isBeingRenderedInsideMobileNav]
  );

  if (
    !isLoggedIn ||
    (!globalBitbucketActions?.length && !containerBitbucketActions?.length)
  ) {
    return null;
  }

  return (
    <styled.HorizontalNavItem>
      <Popup
        isOpen={isOpen}
        onClose={onClose}
        placement="bottom-start"
        trigger={renderTriggerButton}
        content={renderMenu}
      />
    </styled.HorizontalNavItem>
  );
};

export default memo(injectIntl(HorizontalCreateBase));

const HorizontalCreate = memo(injectIntl(HorizontalCreateBase));

export { HorizontalCreate };
